import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { BrandLoader } from '@/components/page/brand-loader';
import { fetchCurrentAccountUncached } from '@/services/auth/account-api';
import { MAX_TRANSPORT_FAILURES, useAccountReadiness } from '@/services/auth/account-readiness';
import { storePendingSignupCompletion } from '@/services/analytics/signup-completed';
// oxlint-disable-next-line local-rules/enforce-feature-boundaries -- the barrel would pull the copilot runtime into the signup chunk
import { selectPostSignupDestination } from '@/features/onboarding/post-signup-destination';
// oxlint-disable-next-line local-rules/enforce-feature-boundaries -- same chunk concern as above
import { ONBOARDING_PATHS } from '@/features/onboarding/paths';
// oxlint-disable-next-line local-rules/enforce-feature-boundaries -- same chunk concern as above
import { WORKSPACE_SETUP_LABEL } from '@/features/onboarding/agent-handoff-labels';
import { toSignupCompletedEvent, trackSignupCompleted } from './utils/analytics';
import type { SignupCompletion } from './types';

export { MAX_TRANSPORT_FAILURES };

type PostRegistrationWaitProps = {
  completion: SignupCompletion;
};

/**
 * Post-registration landing. One uncached account read picks the destination;
 * the wizard cohort moves on immediately, everyone else waits for the workspace.
 */
export function PostRegistrationWait({ completion }: PostRegistrationWaitProps) {
  const { data: accountResponse, isError } = useQuery({
    queryKey: ['signup', 'post-registration-account'],
    queryFn: ({ signal }) => fetchCurrentAccountUncached(signal),
    retry: MAX_TRANSPORT_FAILURES,
    retryDelay: 1000,
    staleTime: 0,
    gcTime: 0,
  });

  if (accountResponse == null) {
    // The give-up path has no payload to choose from: the account was created,
    // but nothing could be read, so the app's own bootstrap reports the failure.
    if (isError) {
      return <SafeNavigate to="/" options={{ shouldReplaceHistoryEntry: true }} />;
    }
    return <BrandLoader label={WORKSPACE_SETUP_LABEL} isEmbedded={true} />;
  }

  if (selectPostSignupDestination(accountResponse.account) === ONBOARDING_PATHS.wizard) {
    return <WizardHandoff completion={completion} />;
  }
  return <WorkspaceReadinessWait completion={completion} />;
}

/** Park the event for the root readiness observer and go — no wait. */
function WizardHandoff({ completion }: PostRegistrationWaitProps) {
  const hasStoredRef = useRef(false);
  if (!hasStoredRef.current) {
    hasStoredRef.current = true;
    storePendingSignupCompletion(toSignupCompletedEvent(completion));
  }
  return <SafeNavigate to={ONBOARDING_PATHS.wizard} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * Hold until the account payload carries an accessible environment and that
 * environment loads, fire `Signup: Completed`, then redirect into the app.
 */
function WorkspaceReadinessWait({ completion }: PostRegistrationWaitProps) {
  const { readyResponse, isError } = useAccountReadiness();
  const isReady = readyResponse != null;

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (!isReady || hasTrackedRef.current) {
      return;
    }
    hasTrackedRef.current = true;
    trackSignupCompleted(completion);
  }, [isReady, completion]);

  // A poll that gave up redirects without the event: the account was created,
  // but readiness was never observed, and Ember only fired on a good poll.
  if (!isReady && !isError) {
    return <BrandLoader label={WORKSPACE_SETUP_LABEL} isEmbedded={true} />;
  }

  return <SafeNavigate to="/" options={{ shouldReplaceHistoryEntry: true }} />;
}
